<portal-page-heading>
  {{ 'meetings.title' | translate }}

  <ng-container actions>
    <!-- Add new conference -->
    <portal-icon-button
      *portalIsAuthorized="{
        anyPermission: [Permission.MANAGE_CONFERENCE, Permission.MANAGE_ANY_CONFERENCE],
      }"
      (onClick)="add()"
    ></portal-icon-button>

    <!-- Refresh entries -->
    <portal-icon-button
      [loading]="viewModel.loading$ | async"
      [portalTippy]="'generic.reload' | translate"
      (onClick)="refresh()"
      iconClass="far fa-sync"
    ></portal-icon-button>
  </ng-container>
</portal-page-heading>

<portal-loading-indicator-container [loading]="viewModel.loading$ | async">
  <portal-errors [errors]="viewModel.error$ | async"></portal-errors>

  @if (hasMeetings$ | async) {
    <div>
      @if (list.entries.length > 0) {
        <portal-filter-input
          #filter="filter"
          [calculateMetaData]="calculateMetaData"
          [entries]="list.entries"
          [properties]="metaDataProperties"
          (changed)="filterChanged($event)"
          portalDataTableFilter
        ></portal-filter-input>
      }
      @if (list.filteredEntries.length > 0) {
        <div class="container-fluid mt-4 p-0">
          <div class="row">
            @for (meeting of (list.page$ | async)?.entries; track meeting) {
              <portal-card class="col-12 col-md-6">
                <div class="d-flex flex-grow-1" header>
                  <div class="flex-grow-1">
                    <h2>{{ meeting.name }}</h2>
                    <div class="text-muted font-xs">
                      @if (meeting.owner != null) {
                        <span>
                          {{
                            'meeting.owner'
                              | translate: { name: meeting.owner | contactName: { type: 'short' } }
                          }}
                        </span>
                      } @else {
                        @if ((isMultiSite$ | async) === false) {
                          <span>
                            {{ 'meeting.enterpriseMeeting' | translate }}
                          </span>
                        }
                        @if (isMultiSite$ | async) {
                          <span>
                            {{ 'meeting.site' | translate: { site: meeting.site } }}
                          </span>
                        }
                      }
                      @if (meeting.owner != null) {
                        <span>
                          {{
                            'meeting.owner'
                              | translate: { name: meeting.owner | contactName: { type: 'short' } }
                          }}
                        </span>
                      } @else {
                        @if ((isMultiSite$ | async) === false) {
                          <span>
                            {{ 'meeting.enterpriseMeeting' | translate }}
                          </span>
                        } @else {
                          <span> {{ 'meeting.site' | translate: { site: meeting.site } }} </span>
                        }
                      }
                    </div>
                  </div>
                  <span class="d-flex align-items-center gap-2 me-2">
                    @if (meeting.dialIn) {
                      <i class="far fa-phone" [portalTippy]="'meeting.dialIn' | translate"></i>
                    }
                    @if (meeting.videoMode) {
                      <i class="far fa-video" [portalTippy]="'meeting.videoMode' | translate"></i>
                    }
                  </span>
                </div>
                <ng-container
                  *portalIsAuthorized="{
                    anyPermission: [Permission.MANAGE_CONFERENCE, Permission.MANAGE_ANY_CONFERENCE],
                  }"
                  actions
                >
                  <portal-dropdown-menu>
                    <button (click)="edit(meeting)" ngbDropdownItem>
                      <i class="far fa-pencil fa-fw"></i>
                      <span>{{ 'meeting.edit' | translate }}</span>
                    </button>
                    <button class="dropdown-item--danger" (click)="delete(meeting)" ngbDropdownItem>
                      <i class="far fa-trash fa-fw"></i>
                      <span>{{ 'meeting.delete' | translate }}</span>
                    </button>
                    <div class="dropdown-divider"></div>
                    <button (click)="openResetDialog(meeting.id)" ngbDropdownItem>
                      <i class="far fa-sync fa-fw"></i>
                      <span>{{ 'meeting.resetPinOrToken' | translate }}</span>
                    </button>
                  </portal-dropdown-menu>
                </ng-container>
                <dl class="mb-0">
                  <dd>
                    @if (meeting.start != null) {
                      <div>
                        <i class="far fa-clock fa-fw text-start me-1"></i>
                        <span>{{ meeting.start | portalDate: 'dd.MM.yyyy HH:mm' }}</span>
                        @if (meeting.end != null) {
                          <span> - {{ meeting.end | portalDate: 'HH:mm' }} </span>
                        }
                      </div>
                    }
                    @if (meeting.start == null) {
                      <div>
                        <i class="far fa-clock fa-fw text-start me-1"></i>
                        <span>{{ 'meeting.permanent' | translate }}</span>
                      </div>
                    }
                    @if (meeting.userUrl != null) {
                      <div class="d-flex overflow-hidden text-nowrap align-items-center">
                        <i class="far fa-link fa-fw text-start me-1"></i>
                        <a class="text-truncate" [href]="meeting.userUrl">
                          {{ meeting.userUrl }}
                        </a>
                        <portal-copy-to-clipboard [text]="meeting.userUrl"></portal-copy-to-clipboard>
                      </div>
                    }
                    @if (meeting.userPassphrase) {
                      <div>
                        <i class="far fa-key fa-fw text-start me-1"></i>
                        <span>{{ meeting.userPassphrase }}</span>
                      </div>
                    }
                  </dd>
                  @if (meeting.dialIn) {
                    <dt>
                      <span>{{ 'meeting.dialIn.label' | translate }}</span>
                      <portal-copy-to-clipboard
                        class="ms-1"
                        [text]="(meeting.phoneNumber | phoneNumber: 'clean') + ' PIN: ' + meeting.userPin"
                      ></portal-copy-to-clipboard>
                    </dt>
                    <dd>
                      <ul class="list-unstyled d-flex gap-3 mb-0">
                        @if (meeting.phoneNumber != null) {
                          <li>{{ meeting.phoneNumber | phoneNumber }}</li>
                        }
                        <li>{{ 'meeting.dialIn.pin' | translate: { pin: meeting.userPin } }}</li>
                        @if (meeting.adminPin != null) {
                          <li>
                            {{ 'meeting.dialIn.adminPin' | translate: { pin: meeting.adminPin } }}
                          </li>
                        }
                      </ul>
                    </dd>
                  }
                  @if (meeting.members.length > 0) {
                    <dt>{{ 'meeting.members' | translate }}</dt>
                    <dd class="mb-0">
                      <ul class="meeting__member-list list-comma">
                        @for (member of meeting.members; track member) {
                          <li class="meeting__member">
                            <span class="member__name">{{
                              member | contactName: { type: 'abbreviated' }
                            }}</span>
                            @if (member.memberRole === MemberRole.ADMIN) {
                              <span
                                class="badge text-bg-info ms-1"
                                [portalTippy]="'meeting.member.adminBadge.info' | translate"
                              >
                                {{ 'meeting.member.adminBadge' | translate }}
                              </span>
                            }
                            @if (member.memberRole === MemberRole.OWNER) {
                              <span
                                class="badge text-bg-info ms-1"
                                [portalTippy]="'meeting.member.ownerBadge.info' | translate"
                              >
                                {{ 'meeting.member.ownerBadge' | translate }}
                              </span>
                            }
                          </li>
                        }
                      </ul>
                    </dd>
                  }
                </dl>
                <button
                  class="btn btn-primary meeting__primary-action--floating"
                  [portalTippy]="'meeting.copyInvitation' | translate"
                  (click)="copyInvitation(meeting)"
                >
                  <i class="far fa-copy"></i>
                </button>
              </portal-card>
            }
          </div>
        </div>
      }
      @if ((list.page$ | async)?.pages > 1) {
        <portal-pagination
          class="mt-2"
          [page]="list.page$ | async"
          (onPageChanged)="pageChanged($event)"
        ></portal-pagination>
      }
    </div>
  } @else {
    <div>{{ 'meetings.noMeetings' | translate }}</div>
  }
</portal-loading-indicator-container>
